import { ChevronDown, ChevronRight, Star } from 'lucide-react';
import { VARIABLES } from '@app/components/common/constants';
import { VariableCard } from './VariableCard';

type VariableId = (typeof VARIABLES)[number]['id'];

interface VariableSectionProps {
  selectedParam: VariableId;
  onParamChange: (param: VariableId) => void;
  favoriteVariables: Set<string>;
  onToggleFavorite: (varId: string) => void;
  loading: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export function VariableSection({
  selectedParam,
  onParamChange,
  favoriteVariables,
  onToggleFavorite,
  loading,
  expanded = false,
  onExpandedChange,
}: VariableSectionProps) {
  // Get time series variables
  const timeSeriesVariables = VARIABLES.filter((v) => v.showTimeSeries);

  // Sort variables: favorites first, then alphabetically
  const sortedVariables = [
    ...timeSeriesVariables.filter((v) => favoriteVariables.has(v.id)),
    ...timeSeriesVariables.filter((v) => !favoriteVariables.has(v.id)),
  ];

  const selectedVariable = timeSeriesVariables.find((v) => v.id === selectedParam);
  const favoriteCount = timeSeriesVariables.filter((v) => favoriteVariables.has(v.id)).length;

  return (
    <div>
      <button
        onClick={() => onExpandedChange && onExpandedChange(!expanded)}
        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
          <div className="text-left">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Weather Parameters
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {selectedVariable ? selectedVariable.label.split(' [')[0] : 'Select parameter'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {favoriteCount > 0 && (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 text-pink-500 fill-current" />
              {favoriteCount}
            </span>
          )}
          <span>{timeSeriesVariables.length} total</span>
        </div>
      </button>

      {expanded && (
        <div className="mt-2 space-y-2 pl-7">
          {sortedVariables.map((variable) => (
            <VariableCard
              key={variable.id}
              variable={variable}
              isSelected={selectedParam === variable.id}
              isFavorite={favoriteVariables.has(variable.id)}
              onSelect={() => onParamChange(variable.id as VariableId)}
              onToggleFavorite={() => onToggleFavorite(variable.id)}
              disabled={loading}
            />
          ))}
        </div>
      )}
    </div>
  );
}
